import { useState, useEffect } from "react";
import api from "../../services/api";
import { Plus, Check, Trash2, Crown, Layers } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

export default function AdminSaaSPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: "",
    price: 0,
    billing_cycle: "monthly",
    max_students: 100,
    description: "",
    is_popular: false,
    features: "Up to 100 Students, Custom Subdomain, Basic Support"
  });

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await api.get("/saas/plans");
      setPlans(res.data);
    } catch (err) {
      toast.error("Failed to load SaaS plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        features: typeof form.features === "string" ? form.features.split(",").map(f => f.trim()).filter(f => f) : form.features
      };
      
      if (form.id) {
        await api.put(`/saas/plans/${form.id}`, payload);
        toast.success("Plan updated successfully!");
      } else {
        await api.post("/saas/plans", payload);
        toast.success("Plan created successfully!");
      }
      setShowModal(false);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save plan");
    }
  };

  const openCreateModal = () => {
    setForm({
      name: "",
      price: 0,
      billing_cycle: "monthly",
      max_students: 100,
      description: "",
      is_popular: false,
      features: "Up to 100 Students, Custom Subdomain, Basic Support"
    });
    setShowModal(true);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    try {
      await api.delete(`/saas/plans/${planId}`);
      toast.success("Plan deleted");
      fetchPlans();
    } catch (err) {
      toast.error("Failed to delete plan");
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-8">
      <Toaster position="top-right" />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white flex items-center gap-3">
            <Crown size={24} className="text-indigo-600 dark:text-indigo-400" />
            SaaS Subscription Plans
          </h1>
          <p className="text-slate-500 mt-1">Define the pricing tiers that Institutes subscribe to.</p>
        </div>
        <button
          onClick={openCreateModal}
          className="btn-primary"
        >
          <Plus size={18} /> Create New Tier
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600/20 border-t-indigo-600" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {plans.map((plan, index) => {
            const isPopular = plan.is_popular; // Highlight based on DB field
            const isCustom = plan.price === 0 || !plan.price;
            
            return (
              <div key={plan.id} className={`group relative flex flex-col rounded-[2.5rem] bg-white p-8 dark:bg-slate-900 transition-all ${isPopular ? 'border-2 border-indigo-500 shadow-2xl shadow-indigo-500/10' : 'border border-slate-100 shadow-xl shadow-slate-200/50 dark:border-slate-800 dark:shadow-none'}`}>
                
                {/* Delete Action */}
                <button 
                  onClick={() => handleDelete(plan.id)}
                  className="absolute top-6 right-6 p-2 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-full transition-colors z-10 dark:hover:bg-rose-950"
                  title="Delete Plan"
                >
                  <Trash2 size={18} />
                </button>

                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-purple-500 to-indigo-300 px-4 py-1 text-[10px] font-black uppercase tracking-wider text-white shadow-md">
                    Most Popular
                  </div>
                )}
                
                {/* Plan Header */}
                <h3 className="pr-10 text-[1.4rem] font-black text-slate-900 dark:text-white">{plan.name}</h3>
                <p className="mt-3 text-[13px] font-semibold text-slate-500 leading-relaxed min-h-[60px]">
                  {plan.description || (isPopular ? "Optimized for test preparation centers, competitive classes, and high-frequency exam systems." : (isCustom ? "For state universities, large EdTech companies, and multi-national training centers." : "Perfect for primary and secondary educational campuses looking to manage online studies."))}
                </p>

                {/* Price */}
                <div className="mt-6 mb-10 flex items-baseline">
                  {isCustom ? (
                    <span className="text-[3.2rem] font-black tracking-tight text-slate-900 dark:text-white leading-none">Custom</span>
                  ) : (
                    <>
                      <span className="text-[3.2rem] font-black tracking-tight text-slate-900 dark:text-white leading-none">₹{plan.price.toLocaleString()}</span>
                      <div className="ml-2 flex flex-col">
                        <span className="text-xs font-bold text-slate-400">/ month</span>
                        <span className="text-[10px] font-bold text-slate-400">(annual)</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Features */}
                <div className="flex-1 space-y-4 mb-10">
                  <div className="flex items-start gap-3">
                    <Check size={16} strokeWidth={3} className="mt-0.5 text-indigo-500 shrink-0" />
                    <span className="text-[13px] font-bold text-slate-600 dark:text-slate-300">{isCustom ? "Unlimited students & bandwidth" : `Up to ${plan.max_students.toLocaleString()} active students`}</span>
                  </div>
                  {plan.features?.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check size={16} strokeWidth={3} className="mt-0.5 text-indigo-500 shrink-0" />
                      <span className="text-[13px] font-bold text-slate-600 dark:text-slate-300">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Button */}
                <button 
                  onClick={() => {
                    // Open edit modal
                    setForm({ ...plan, features: plan.features ? plan.features.join(", ") : "" });
                    setShowModal(true);
                  }}
                  className={`mt-auto w-full rounded-2xl py-4 text-[13px] font-black transition-all ${isPopular ? 'bg-gradient-to-r from-indigo-500 to-blue-600 text-white hover:opacity-90 shadow-lg shadow-indigo-500/25' : 'bg-slate-50 text-slate-900 hover:bg-slate-100 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700'}`}
                >
                  Edit Plan
                </button>
              </div>
            );
          })}
          {plans.length === 0 && (
            <div className="col-span-full py-16 text-center text-slate-500 border-2 border-dashed border-slate-200 rounded-2xl dark:border-slate-800">
              <Layers size={48} className="mx-auto mb-4 opacity-20" />
              <p className="text-lg font-medium text-slate-900 dark:text-white">No plans configured yet</p>
              <p className="mt-1 text-sm">Create your first SaaS pricing tier to start monetizing.</p>
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900 border dark:border-slate-800">
            <h2 className="mb-4 text-xl font-bold text-slate-900 dark:text-white">Create SaaS Tier</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">Plan Name</label>
                <input
                  type="text"
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Starter, Pro, Enterprise"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">Price (INR)</label>
                  <input
                    type="number"
                    required
                    min="0"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">Billing Cycle</label>
                  <select
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                    value={form.billing_cycle}
                    onChange={(e) => setForm({ ...form, billing_cycle: e.target.value })}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">Max Students Allowed</label>
                <input
                  type="number"
                  required
                  min="1"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                  value={form.max_students}
                  onChange={(e) => setForm({ ...form, max_students: Number(e.target.value) })}
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">Description / Subtitle</label>
                <textarea
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white resize-none"
                  rows="2"
                  value={form.description || ""}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Perfect for primary and secondary educational campuses..."
                ></textarea>
              </div>

              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">Features (Comma separated)</label>
                <input
                  type="text"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                  value={form.features || ""}
                  onChange={(e) => setForm({ ...form, features: e.target.value })}
                  placeholder="e.g. Custom Subdomain, API Access"
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="is_popular"
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600"
                  checked={form.is_popular || false}
                  onChange={(e) => setForm({ ...form, is_popular: e.target.checked })}
                />
                <label htmlFor="is_popular" className="text-sm font-semibold text-slate-700 dark:text-slate-300 cursor-pointer">
                  Mark as "Most Popular"
                </label>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 rounded-xl bg-slate-100 py-3 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-xl bg-indigo-600 py-3 text-sm font-bold text-white transition-colors hover:bg-indigo-700"
                >
                  Save Plan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
